import React, { useContext, useState, useEffect, useRef } from "react";
import Moment from "moment";

import { CustomersContext } from "../../context/customersContext";
import { SalesContext } from "../../context/salesContext";
import CustomerModal from "../modals/CustomerModal";
import SaleModal from "../modals/SaleModal";
import DoorRender from "./SaleDoorRender";
import SpecDoorFullscreen from "./SpecDoorFullscreen";
import statusList from "../../data/statusList.json";
import PaymentModal from "../modals/PaymentModal";
import NoteModal from "../modals/NoteModal";
import { Badge, Button, Select } from "../ui";
import { STATUS_TONE } from "../../constants/saleStatus";

// ─── presentational helpers ──────────────────────────────────────────────────

const Field = ({ label, value, highlight, fullWidth }) => (
    <div className={`min-w-0 ${fullWidth ? "col-span-2" : ""}`}>
        <dt className="detail-field-label">{label}</dt>
        <dd
            className={`mt-0.5 break-words text-sm ${
                highlight ? "font-semibold text-danger-600" : "text-ink-900"
            }`}
        >
            {value || "—"}
        </dd>
    </div>
);

const SubSection = ({ title, children }) => (
    <div className="pt-4">
        <div className="mb-3 flex items-center gap-2">
            <div className="h-px flex-1 bg-ink-100" />
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                {title}
            </span>
            <div className="h-px flex-1 bg-ink-100" />
        </div>
        {children}
    </div>
);

const TabButton = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            active
                ? "bg-brand-600 text-white shadow-sm"
                : "text-ink-500 hover:text-ink-700"
        }`}
    >
        {children}
    </button>
);

/** True if this door side has any saved data (not only doorMaterial — legacy rows may omit it). */
function doorSideHasContent(d) {
    if (!d || typeof d !== "object") return false;
    const h = d.hinge || {};
    const l = d.lock || {};
    const ins = d.doorInsert || {};
    const t = (v) => v != null && String(v).trim() !== "";
    return (
        t(d.doorMaterial) ||
        t(d.doorBrand) ||
        t(d.doorStyle) ||
        t(d.doorSlabWidth) ||
        t(d.doorSlabHeight) ||
        t(d.paintOut) ||
        t(d.paintIn) ||
        t(d.open) ||
        t(h.hingeColour) ||
        t(h.position) ||
        t(h.selfClosing) ||
        t(l.lockType) ||
        t(l.lockStyle) ||
        t(l.distanceFromTop) ||
        ins.doorInsert === "Yes" ||
        t(ins.design) ||
        t(ins.width) ||
        t(ins.height) ||
        t(ins.quantity) ||
        t(ins.position)
    );
}

function formatDoorSlab(door, getFraction) {
    if (!door) return null;
    const parts = [door.doorMaterial, door.doorBrand, door.doorStyle].filter(Boolean);
    const head = parts.join(" — ");
    if (
        door.doorSlabWidth != null &&
        String(door.doorSlabWidth) !== "" &&
        door.doorSlabHeight != null &&
        String(door.doorSlabHeight) !== ""
    ) {
        const dims = `${getFraction(door.doorSlabWidth)}" × ${getFraction(door.doorSlabHeight)}"`;
        return head ? `${head} — ${dims}` : dims;
    }
    return head || null;
}

function formatLock(lock) {
    if (!lock) return null;
    const type = lock.lockType || "";
    const dist = lock.distanceFromTop ? ` (${lock.distanceFromTop} from top)` : "";
    const style = lock.lockStyle ? ` — ${lock.lockStyle}` : "";
    const out = `${type}${dist}${style}`.trim();
    return out || null;
}

function formatHinge(hinge) {
    if (!hinge) return null;
    const parts = [];
    if (hinge.hingeColour) parts.push(hinge.hingeColour);
    if (hinge.position) parts.push(`${hinge.position} hinge`);
    if (hinge.selfClosing === "Yes") parts.push("Self closing");
    return parts.length ? parts.join(" · ") : null;
}

function formatDoorOpen(open) {
    if (open == null || String(open).trim() === "") return null;
    if (open === "Yes") return "Yes";
    if (open === "No") return "No";
    return String(open);
}

const ListItem = ({ children, onRemove }) => (
    <div className="group relative rounded-lg border border-ink-100 bg-ink-50 p-3 pr-9">
        {children}
        {onRemove && (
            <button
                type="button"
                onClick={onRemove}
                aria-label="Remove"
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-ink-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-ink-100 hover:text-ink-700"
            >
                <i className="fas fa-times text-xs" aria-hidden="true" />
            </button>
        )}
    </div>
);

const EmptyList = ({ children }) => (
    <div className="rounded-lg border border-dashed border-ink-200 bg-ink-50 px-4 py-8 text-center text-sm text-ink-500">
        {children}
    </div>
);

const NotesList = ({ notes, onRemove }) => {
    if (!notes || notes.length === 0) return <EmptyList>No notes yet.</EmptyList>;
    return (
        <div className="space-y-2">
            {notes
                .slice(0)
                .reverse()
                .map((note, idx) => (
                    <ListItem key={idx} onRemove={() => onRemove(idx)}>
                        <p className="text-xs font-medium text-ink-500">
                            {Moment(note.date).format("LLL")}
                        </p>
                        <p className="mt-1 text-sm text-ink-800">{note.description}</p>
                    </ListItem>
                ))}
        </div>
    );
};

const ActivityList = ({ activity }) => {
    if (!activity || activity.length === 0) return <EmptyList>No activity yet.</EmptyList>;
    return (
        <div className="space-y-2">
            {activity
                .slice(0)
                .reverse()
                .map((entry, idx) => (
                    <ListItem key={idx}>
                        <p className="text-sm text-ink-800">{entry}</p>
                    </ListItem>
                ))}
        </div>
    );
};

// ─── main component ──────────────────────────────────────────────────────────

const WorkOrder = ({ workOrder, setWorkOrder, onSpecDoorFullscreenChange }) => {
    const { customers } = useContext(CustomersContext);
    const { action } = useContext(SalesContext);

    const [customer, setCustomer] = useState(null);
    const [currStatus, setCurrStatus] = useState(workOrder?.workOrder?.status || "");
    const [activeTab, setActiveTab] = useState("info");
    const [showNotesModal, setShowNotesModal] = useState(false);

    const [openCustomerModal, setOpenCustomerModal] = useState(false);
    const [openSaleModal, setOpenSaleModal] = useState(false);
    const [paymentModalShow, setPaymentModalShow] = useState(false);
    const [specDoorFullscreen, setSpecDoorFullscreen] = useState(false);

    const leftColRef = useRef(null);
    const specsScrollRef = useRef(null);
    const [rightMaxHeight, setRightMaxHeight] = useState(undefined);

    useEffect(() => {
        const el = specsScrollRef.current;
        if (!el) return;

        const scrollNearest = (deltaY) => {
            let parent = el.parentElement;
            while (parent) {
                const { overflowY } = window.getComputedStyle(parent);
                if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) {
                    parent.scrollBy({ top: deltaY });
                    return;
                }
                parent = parent.parentElement;
            }
            window.scrollBy({ top: deltaY });
        };

        const onWheel = (e) => {
            const atTop = el.scrollTop === 0 && e.deltaY < 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
            if (!atTop && !atBottom) return;
            e.preventDefault();
            scrollNearest(e.deltaY);
        };

        let lastTouchY = 0;
        const onTouchStart = (e) => { lastTouchY = e.touches[0].clientY; };
        const onTouchMove = (e) => {
            const currentY = e.touches[0].clientY;
            const deltaY = lastTouchY - currentY;
            lastTouchY = currentY;
            const atTop = el.scrollTop === 0 && deltaY < 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && deltaY > 0;
            if (!atTop && !atBottom) return;
            e.preventDefault();
            scrollNearest(deltaY);
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        return () => {
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
        };
    }, []);

    useEffect(() => {
        if (specDoorFullscreen) {
            setRightMaxHeight(undefined);
            return;
        }
        const el = leftColRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            setRightMaxHeight(el.offsetHeight);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [specDoorFullscreen]);

    useEffect(() => {
        setSpecDoorFullscreen(false);
    }, [workOrder._id]);

    const onFullscreenChangeRef = useRef(onSpecDoorFullscreenChange);
    onFullscreenChangeRef.current = onSpecDoorFullscreenChange;
    useEffect(() => {
        if (onFullscreenChangeRef.current) {
            onFullscreenChangeRef.current(specDoorFullscreen);
        }
    }, [specDoorFullscreen]);

    useEffect(() => {
        if (workOrder && customers) {
            setCustomer(customers.find((c) => c._id === workOrder.customerId) || null);
        }
    }, [workOrder, customers]);

    useEffect(() => {
        if (workOrder?._id) {
            setCurrStatus(workOrder.workOrder?.status || "");
        }
    }, [workOrder?._id]);

    // ── handlers ────────────────────────────────────────────────────────────

    const handleStatusDropdown = (e) => {
        setWorkOrder({
            ...workOrder,
            workOrder: { ...workOrder.workOrder, status: e.target.value },
        });
    };

    const handleConfirmStatus = async () => {
        try {
            workOrder.recentActivities.push(
                `${Moment().format("LLL")}: workOrder,status was changed from '${currStatus}' to '${workOrder.workOrder.status}'`,
            );
            await action.updateSale(workOrder._id, workOrder);
            setWorkOrder({ ...workOrder });
            setCurrStatus(workOrder.workOrder.status);
        } catch (error) {
            console.log("Error: ", error);
        }
    };

    const handleCancelStatus = () => {
        setWorkOrder({
            ...workOrder,
            workOrder: { ...workOrder.workOrder, status: currStatus },
        });
    };

    const handleMarkReady = async () => {
        try {
            const nextStatus =
                workOrder.workOrder.installationRequired === "Yes"
                    ? "Ready For Installation"
                    : "Ready For Pick Up";
            workOrder.workOrder.status = nextStatus;
            workOrder.recentActivities.push(
                `${Moment().format("LLL")}: workOrder,status was changed from '${currStatus}' to '${nextStatus}'`,
            );
            await action.updateSale(workOrder._id, workOrder);
            setCurrStatus(nextStatus);
            setWorkOrder({ ...workOrder });
        } catch (error) {
            console.log("Error: ", error);
        }
    };

    const addNote = (note) => {
        workOrder.notes.push(note);
        action.updateSale(workOrder._id, workOrder);
        setWorkOrder({ ...workOrder });
    };

    const removeNote = (reversedIndex) => {
        const updatedNotes = workOrder.notes
            .slice(0)
            .reverse()
            .filter((_, idx) => idx !== reversedIndex)
            .slice(0)
            .reverse();
        workOrder.notes = updatedNotes;
        action.updateSale(workOrder._id, workOrder).then(async () => {
            const refreshed = await action.getSale(workOrder._id);
            if (refreshed) setWorkOrder(refreshed);
        });
    };

    const addPayment = (payment) => {
        workOrder.payments.push(payment);
        action.updateSale(workOrder._id, workOrder);
    };

    // ── derived values ───────────────────────────────────────────────────────

    const spec = workOrder.productSpecification;
    const currentStatus = workOrder.workOrder?.status || "";
    const statusTone = STATUS_TONE[currentStatus] || "neutral";
    const hasPendingChange = currStatus !== currentStatus;
    const isReadyOrComplete =
        currentStatus === "Completed" || currentStatus.startsWith("Ready");

    const balance = (() => {
        let paid = 0;
        workOrder.payments.forEach((p) => { paid += parseInt(p.amount) || 0; });
        return workOrder.salesPrice - paid;
    })();
    const balanceClass =
        balance < 0 ? "text-success-600" : balance > 0 ? "text-danger-600" : "text-ink-500";

    const customerName = customer
        ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
        : "—";

    const renderDoorSide = (title, doorKey) => {
        const door = spec?.[doorKey];
        if (!doorSideHasContent(door)) return null;
        const lock = door.lock || {};
        const hinge = door.hinge || {};
        const ins = door.doorInsert || {};
        const showInsert =
            ins.doorInsert === "Yes" || ins.design || ins.width || ins.height || ins.quantity;

        return (
            <SubSection key={doorKey} title={title}>
                <dl className="grid grid-cols-1 gap-y-3">
                    <Field label="Door slab" value={formatDoorSlab(door, action.getFraction)} />
                    <Field label="Lock" value={formatLock(lock)} />
                    <Field label="Hinge" value={formatHinge(hinge)} />
                    <Field label="Door open" value={formatDoorOpen(door.open)} />
                    <Field label="Paint out" value={door.paintOut} />
                    <Field label="Paint in" value={door.paintIn} />
                    {showInsert && (
                        <>
                            <Field
                                label="Glass insert"
                                value={
                                    ins.doorInsert === "Yes"
                                        ? "Yes"
                                        : ins.doorInsert || null
                                }
                            />
                            <Field label="Insert design" value={ins.design} />
                            <Field
                                label="Insert size"
                                value={
                                    ins.width != null &&
                                    String(ins.width) !== "" &&
                                    ins.height != null &&
                                    String(ins.height) !== ""
                                        ? `${action.getFraction(ins.width)}" × ${action.getFraction(ins.height)}"`
                                        : null
                                }
                            />
                            <Field label="Insert quantity" value={ins.quantity} />
                            <Field label="Insert position" value={ins.position} />
                        </>
                    )}
                </dl>
            </SubSection>
        );
    };

    const specificationsInner = (
        <>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                <Field
                    label="Frame Size"
                    value={
                        spec.frameSize?.width
                            ? `${action.getFraction(spec.frameSize.width)}" × ${action.getFraction(spec.frameSize.height)}"`
                            : null
                    }
                />
                <Field label="Brick to Brick" value={spec.brickToBrick} />
                <Field label="Door system" value={spec.doorSystem} />
                <Field label="Sill" value={spec.sill} />
                <Field label="Sill extension" value={spec.sillExtension} />
                <Field
                    label="Jamb"
                    value={
                        spec.jamb?.jambSize
                            ? `${action.getFraction(spec.jamb.jambSize)}" — ${spec.jamb.jambMaterial}`
                            : null
                    }
                    highlight={
                        spec.jamb?.jambSize != null && spec.jamb.jambSize !== "6.625"
                    }
                />
            </dl>

            {renderDoorSide("Door Left", "doorLeft")}
            {renderDoorSide("Door Middle", "doorMiddle")}
            {renderDoorSide("Door Right", "doorRight")}

            {spec.sidelightLeft?.sidelight === "Yes" && (
                <SubSection title="Sidelight Left">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <Field label="Panel" value={spec.sidelightLeft.panel} />
                        {spec.sidelightLeft.panelWidth && (
                            <Field
                                label="Panel 1"
                                value={`${action.getFraction(spec.sidelightLeft.panelWidth)}" × ${action.getFraction(spec.sidelightLeft.panelHeight)}"`}
                            />
                        )}
                        {spec.sidelightLeft.panelWidth2 && (
                            <Field
                                label="Panel 2"
                                value={`${action.getFraction(spec.sidelightLeft.panelWidth2)}" × ${action.getFraction(spec.sidelightLeft.panelHeight2)}"`}
                            />
                        )}
                        {spec.sidelightLeft.insert === "Yes" && (
                            <Field
                                label="Insert"
                                value={`${spec.sidelightLeft.insertDesign} ${action.getFraction(spec.sidelightLeft.insertWidth)}" × ${action.getFraction(spec.sidelightLeft.insertHeight)}"`}
                            />
                        )}
                    </dl>
                </SubSection>
            )}

            {spec.sidelightRight?.sidelight === "Yes" && (
                <SubSection title="Sidelight Right">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <Field label="Panel" value={spec.sidelightRight.panel} />
                        {spec.sidelightRight.panelWidth && (
                            <Field
                                label="Panel 1"
                                value={`${action.getFraction(spec.sidelightRight.panelWidth)}" × ${action.getFraction(spec.sidelightRight.panelHeight)}"`}
                            />
                        )}
                        {spec.sidelightRight.panelWidth2 && (
                            <Field
                                label="Panel 2"
                                value={`${action.getFraction(spec.sidelightRight.panelWidth2)}" × ${action.getFraction(spec.sidelightRight.panelHeight2)}"`}
                            />
                        )}
                        {spec.sidelightRight.insert === "Yes" && (
                            <Field
                                label="Insert"
                                value={`${spec.sidelightRight.insertDesign} ${action.getFraction(spec.sidelightRight.insertWidth)}" × ${action.getFraction(spec.sidelightRight.insertHeight)}"`}
                            />
                        )}
                    </dl>
                </SubSection>
            )}

            {spec.transom?.transomBox === "Yes" && (
                <SubSection title="Transom">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <Field label="Type" value={spec.transom.transomType} />
                        {spec.transom.transomType === "Arc" && (
                            <>
                                <Field label="Arc Height" value={spec.transom.transomArcHeight} />
                                <Field label="Arc Legs" value={spec.transom.transomArcLegs} />
                            </>
                        )}
                        <Field
                            label="Transom Insert"
                            value={`${spec.transom.transomInsertDesign} ${action.getFraction(spec.transom.transomInsertWidth)}" × ${action.getFraction(spec.transom.transomInsertHeight)}"`}
                            fullWidth
                        />
                    </dl>
                </SubSection>
            )}
        </>
    );


    // ── render ───────────────────────────────────────────────────────────────

    return (
        <div className="space-y-5">
            <SpecDoorFullscreen
                isOpen={specDoorFullscreen}
                onClose={() => setSpecDoorFullscreen(false)}
                specifications={specificationsInner}
                workOrder={workOrder}
            />

            {/* ── Header ── */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold tracking-tight text-ink-900">
                            PO #{workOrder.po}
                        </h2>
                        {currentStatus && (
                            <Badge tone={statusTone}>{currentStatus}</Badge>
                        )}
                    </div>
                    <p className="mt-0.5 text-sm text-ink-500">{customerName}</p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <div className="w-52">
                        <Select
                            id="work-order-status"
                            value={currentStatus}
                            onChange={handleStatusDropdown}
                        >
                            {statusList.map((s) => (
                                <option key={s.status} value={s.status}>
                                    {s.status}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <Button
                        variant={isReadyOrComplete ? "success" : "successOutline"}
                        size="sm"
                        onClick={handleMarkReady}
                        leadingIcon={<i className="far fa-check-circle" aria-hidden="true" />}
                    >
                        Mark Ready
                    </Button>
                </div>
            </div>

            {/* Pending status confirmation */}
            {hasPendingChange && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 sm:px-4 sm:py-3">
                    <p className="text-sm text-warning-800">
                        Change status to{" "}
                        <span className="font-semibold">"{currentStatus}"</span>?
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={handleCancelStatus}
                            className="rounded-md px-3 py-1 text-xs font-medium text-ink-600 transition-colors hover:bg-ink-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmStatus}
                            className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-700"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            )}

            {!specDoorFullscreen && (
                <>
            {/* ── Main 2-col grid ── */}
            <div className="grid min-h-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
                {/* LEFT: fixed viewport band — explicit height so flex child scroll works; self-start avoids grid min-height:auto expanding the row */}
                <section
                    ref={leftColRef}
                    className="flex min-h-0 w-full min-w-0 flex-col self-start overflow-hidden rounded-xl border border-ink-100 bg-white"
                    style={{
                        /* Explicit height: max-h alone does not cap grid/flex min-content; keeps panel within viewport (incl. modals ~80vh) */
                        height: "min(70vh, calc(100dvh - 12rem))",
                        maxHeight: "min(70vh, calc(100dvh - 12rem))",
                    }}
                >
                    <h3 className="detail-section-title shrink-0 border-b border-ink-100 px-3 py-3 sm:px-5 sm:py-4">
                        Specifications
                    </h3>
                    <div ref={specsScrollRef} className="min-h-0 flex-1 basis-0 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4">
                        {specificationsInner}
                    </div>
                </section>

                {/* RIGHT: capped to left column height, scrolls when content overflows */}
                <section
                    className="rounded-xl border border-ink-100 bg-white p-3 sm:p-5"
                    style={rightMaxHeight ? { height: rightMaxHeight, overflowY: "auto" } : undefined}
                >
                    <div className="mb-4 flex items-center gap-2">
                        <div className="flex gap-1 rounded-lg bg-ink-50 p-1">
                            <TabButton
                                active={activeTab === "info"}
                                onClick={() => setActiveTab("info")}
                            >
                                Notes &amp; Info
                            </TabButton>
                            <TabButton
                                active={activeTab === "activity"}
                                onClick={() => setActiveTab("activity")}
                            >
                                Activity
                            </TabButton>
                        </div>
                    </div>

                    {activeTab === "info" && (
                        <div className="flex flex-col gap-5">
                            {/* Balance + payment */}
                            <div className="flex items-center justify-between rounded-lg border border-ink-100 bg-ink-50 px-3 py-2.5 sm:px-4 sm:py-3">
                                <div>
                                    <p className="detail-field-label">Balance Due</p>
                                    <p className={`text-lg font-semibold tabular-nums ${balanceClass}`}>
                                        ${balance.toLocaleString()}
                                    </p>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPaymentModalShow(true)}
                                    leadingIcon={<i className="fas fa-plus" aria-hidden="true" />}
                                >
                                    Add Payment
                                </Button>
                            </div>

                            {/* Notes */}
                            <div className="pt-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <p className="detail-section-title">Notes</p>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setShowNotesModal(true)}
                                        leadingIcon={<i className="fas fa-plus" aria-hidden="true" />}
                                    >
                                        Add Note
                                    </Button>
                                </div>
                                <NotesList notes={workOrder.notes} onRemove={removeNote} />
                            </div>

                            {/* Quick links */}
                            <div className="border-t border-ink-100 pt-4">
                                <p className="detail-section-title mb-3">Related Records</p>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setOpenCustomerModal(true)}
                                        leadingIcon={<i className="fas fa-user" aria-hidden="true" />}
                                    >
                                        Customer Details
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setOpenSaleModal(true)}
                                        leadingIcon={<i className="fas fa-shopping-cart" aria-hidden="true" />}
                                    >
                                        Sale Details
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "activity" && (
                        <ActivityList activity={workOrder.recentActivities} />
                    )}
                </section>
            </div>

            {/* ── Door preview (full width; min-w-0 so narrow modals/viewports can shrink & scroll) ── */}
            <section className="relative min-w-0 overflow-hidden rounded-xl border border-ink-100 bg-white p-0 md:p-5">
                {workOrder.product === "Door" && (
                    <button
                        type="button"
                        onClick={() => setSpecDoorFullscreen(true)}
                        className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-lg border border-ink-200 bg-white text-sm text-ink-500 shadow-sm transition-colors hover:bg-ink-50 hover:text-ink-700 md:right-4 md:top-4"
                        aria-label="Expand specifications and door preview"
                        title="Fullscreen"
                    >
                        <i className="fas fa-expand" aria-hidden="true" />
                    </button>
                )}
                <h3
                    className={`detail-section-title mb-2 px-3 pt-3 md:mb-4 md:px-0 md:pt-0 ${
                        workOrder.product === "Door" ? "pr-12 md:pr-0" : ""
                    }`}
                >
                    Door Preview
                </h3>
                <DoorRender selectedSale={workOrder} />
            </section>
            </>
            )}

            {/* ── Modals ── */}
            <NoteModal
                showNotesModal={showNotesModal}
                setShowNotesModal={setShowNotesModal}
                addNote={addNote}
            />
            <PaymentModal
                paymentModalShow={paymentModalShow}
                setPaymentModalShow={setPaymentModalShow}
                addPayment={addPayment}
            />
            <CustomerModal
                selectedCustomer={customer}
                open={openCustomerModal}
                setOpen={() => setOpenCustomerModal(true)}
                handleClose={() => setOpenCustomerModal(false)}
            />
            <SaleModal
                selectedSale={workOrder}
                setSelectedSale={setWorkOrder}
                open={openSaleModal}
                setOpen={() => setOpenSaleModal(true)}
                handleClose={() => setOpenSaleModal(false)}
            />
        </div>
    );
};

export default WorkOrder;
